import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

const ImageEditor = ({
  initialImageUrl,
  onImageSaved,
  onCancel,
  credencialId,
  nombres,
  apellidoPaterno,
  apellidoMaterno,
  tipoEmpleado,
}) => {
  const [originalImage, setOriginalImage] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropSize, setCropSize] = useState(200);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCorner, setResizeCorner] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [finalPreview, setFinalPreview] = useState(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

  const cropAreaRef = useRef(null);
  const imageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const CONTAINER_SIZE = 400;

  useEffect(() => {
    if (
      initialImageUrl &&
      initialImageUrl !== 'null' &&
      initialImageUrl !== ''
    ) {
      const cacheBusterUrl = initialImageUrl.includes('?')
        ? `${initialImageUrl}&t=${new Date().getTime()}`
        : `${initialImageUrl}?t=${new Date().getTime()}`;
      loadImageFromUrl(cacheBusterUrl);
    }
  }, [initialImageUrl]);

  useEffect(() => {
    if (originalImage) {
      generateFinalPreview();
    }
  }, [cropPosition, originalImage, displaySize, cropSize]);

  const calculateMaxCropSize = () => {
    if (displaySize.width === 0 || displaySize.height === 0) return 200;
    const maxSize = Math.min(
      displaySize.width,
      displaySize.height,
      CONTAINER_SIZE
    );
    return Math.floor(maxSize);
  };

  const constrainCropPosition = (pos, size = cropSize) => {
    const imageLeft = (CONTAINER_SIZE - displaySize.width) / 2;
    const imageTop = (CONTAINER_SIZE - displaySize.height) / 2;

    const minX = imageLeft;
    const maxX = imageLeft + displaySize.width - size;
    const minY = imageTop;
    const maxY = imageTop + displaySize.height - size;

    return {
      x: Math.min(Math.max(pos.x, minX), maxX),
      y: Math.min(Math.max(pos.y, minY), maxY),
    };
  };

  const constrainCropSize = (newSize, position) => {
    const imageLeft = (CONTAINER_SIZE - displaySize.width) / 2;
    const imageTop = (CONTAINER_SIZE - displaySize.height) / 2;

    const minSize = 80;
    const maxSizeByWidth = displaySize.width;
    const maxSizeByHeight = displaySize.height;
    const maxSize = Math.min(maxSizeByWidth, maxSizeByHeight, CONTAINER_SIZE);

    let constrainedSize = Math.min(Math.max(newSize, minSize), maxSize);

    let newX = position.x;
    let newY = position.y;

    if (newX + constrainedSize > imageLeft + displaySize.width) {
      newX = imageLeft + displaySize.width - constrainedSize;
    }
    if (newY + constrainedSize > imageTop + displaySize.height) {
      newY = imageTop + displaySize.height - constrainedSize;
    }
    if (newX < imageLeft) {
      newX = imageLeft;
    }
    if (newY < imageTop) {
      newY = imageTop;
    }

    return { size: constrainedSize, position: { x: newX, y: newY } };
  };

  const calculateDisplaySize = (imgWidth, imgHeight) => {
    if (!imgWidth || !imgHeight) return { width: 0, height: 0 };

    const ratio = imgWidth / imgHeight;
    let displayWidth, displayHeight;

    if (ratio > 1) {
      displayWidth = CONTAINER_SIZE;
      displayHeight = CONTAINER_SIZE / ratio;
    } else {
      displayHeight = CONTAINER_SIZE;
      displayWidth = CONTAINER_SIZE * ratio;
    }

    return { width: displayWidth, height: displayHeight };
  };

  // Función para resetear el estado de la imagen
  const resetImageState = () => {
    setOriginalImage(null);
    setOriginalImageUrl(null);
    setFinalPreview(null);
    setCropPosition({ x: 0, y: 0 });
    setCropSize(200);
    setDisplaySize({ width: 0, height: 0 });
    setImageSize({ width: 0, height: 0 });
    setError('');
  };

  const loadImageFromUrl = (url) => {
    resetImageState();
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      setOriginalImage(img);
      setOriginalImageUrl(url);
      setImageSize({ width: img.width, height: img.height });
      const newDisplaySize = calculateDisplaySize(img.width, img.height);
      setDisplaySize(newDisplaySize);

      const imageLeft = (CONTAINER_SIZE - newDisplaySize.width) / 2;
      const imageTop = (CONTAINER_SIZE - newDisplaySize.height) / 2;
      const initialSize = Math.min(
        newDisplaySize.width,
        newDisplaySize.height,
        200
      );
      const initialX = imageLeft + (newDisplaySize.width - initialSize) / 2;
      const initialY = imageTop + (newDisplaySize.height - initialSize) / 2;

      setCropSize(initialSize);
      setCropPosition({ x: initialX, y: initialY });
    };
    img.onerror = () => {
      setError('Error al cargar la imagen');
    };
    img.src = url;
  };

  const loadImageFromFile = (file) => {
    resetImageState();
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setOriginalImageUrl(e.target.result);
        setImageSize({ width: img.width, height: img.height });
        const newDisplaySize = calculateDisplaySize(img.width, img.height);
        setDisplaySize(newDisplaySize);

        const imageLeft = (CONTAINER_SIZE - newDisplaySize.width) / 2;
        const imageTop = (CONTAINER_SIZE - newDisplaySize.height) / 2;
        const initialSize = Math.min(
          newDisplaySize.width,
          newDisplaySize.height,
          200
        );
        const initialX = imageLeft + (newDisplaySize.width - initialSize) / 2;
        const initialY = imageTop + (newDisplaySize.height - initialSize) / 2;

        setCropSize(initialSize);
        setCropPosition({ x: initialX, y: initialY });
        setError('');
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const generateFinalPreview = () => {
    if (!originalImage || !previewCanvasRef.current || displaySize.width === 0)
      return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const finalSize = 400;

    canvas.width = finalSize;
    canvas.height = finalSize;

    const scaleX = originalImage.width / displaySize.width;
    const scaleY = originalImage.height / displaySize.height;

    const cropLeft = cropPosition.x;
    const cropTop = cropPosition.y;
    const cropCenterX = cropLeft + cropSize / 2;
    const cropCenterY = cropTop + cropSize / 2;

    const imageLeft = (CONTAINER_SIZE - displaySize.width) / 2;
    const imageTop = (CONTAINER_SIZE - displaySize.height) / 2;

    const pointInUnzoomedX = cropCenterX - imageLeft;
    const pointInUnzoomedY = cropCenterY - imageTop;

    let sourceCenterX = pointInUnzoomedX * scaleX;
    let sourceCenterY = pointInUnzoomedY * scaleY;

    const cropSizeInOriginal = cropSize * scaleX;
    const halfCrop = cropSizeInOriginal / 2;

    sourceCenterX = Math.max(
      halfCrop,
      Math.min(sourceCenterX, originalImage.width - halfCrop)
    );
    sourceCenterY = Math.max(
      halfCrop,
      Math.min(sourceCenterY, originalImage.height - halfCrop)
    );

    const sourceX = sourceCenterX - halfCrop;
    const sourceY = sourceCenterY - halfCrop;

    ctx.clearRect(0, 0, finalSize, finalSize);
    ctx.drawImage(
      originalImage,
      sourceX,
      sourceY,
      cropSizeInOriginal,
      cropSizeInOriginal,
      0,
      0,
      finalSize,
      finalSize
    );

    setFinalPreview(canvas.toDataURL('image/jpeg', 0.9));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar los 5MB');
      return;
    }

    loadImageFromFile(file);

    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChangeImage = () => {
    // Resetear estado actual antes de abrir el selector
    resetImageState();
    // Abrir el selector de archivos
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no debe superar los 5MB');
        return;
      }
      loadImageFromFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - cropPosition.x,
      y: e.clientY - cropPosition.y,
    });
  };

  const handleResizeMouseDown = (e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeCorner(corner);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      originalSize: cropSize,
      originalX: cropPosition.x,
      originalY: cropPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      let newX = e.clientX - dragStart.x;
      let newY = e.clientY - dragStart.y;
      const constrained = constrainCropPosition({ x: newX, y: newY });
      setCropPosition(constrained);
    } else if (isResizing) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      let delta = 0;

      switch (resizeCorner) {
        case 'se':
          delta = Math.max(deltaX, deltaY);
          break;
        case 'sw':
          delta = Math.max(-deltaX, deltaY);
          break;
        case 'ne':
          delta = Math.max(deltaX, -deltaY);
          break;
        case 'nw':
          delta = Math.max(-deltaX, -deltaY);
          break;
        default:
          break;
      }

      let newSize = dragStart.originalSize + delta;
      let newX = dragStart.originalX;
      let newY = dragStart.originalY;

      if (resizeCorner === 'nw') {
        newX = dragStart.originalX - delta;
        newY = dragStart.originalY - delta;
      } else if (resizeCorner === 'ne') {
        newY = dragStart.originalY - delta;
      } else if (resizeCorner === 'sw') {
        newX = dragStart.originalX - delta;
      }

      const constrained = constrainCropSize(newSize, { x: newX, y: newY });
      setCropSize(constrained.size);
      setCropPosition(constrained.position);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeCorner(null);
  };

  const resetCrop = () => {
    const imageLeft = (CONTAINER_SIZE - displaySize.width) / 2;
    const imageTop = (CONTAINER_SIZE - displaySize.height) / 2;
    const newSize = Math.min(displaySize.width, displaySize.height, 200);
    const newX = imageLeft + (displaySize.width - newSize) / 2;
    const newY = imageTop + (displaySize.height - newSize) / 2;

    setCropSize(newSize);
    setCropPosition({ x: newX, y: newY });
  };

  const getCroppedImageBlob = () => {
    return new Promise((resolve, reject) => {
      if (!originalImage) {
        reject(new Error('No hay imagen para recortar'));
        return;
      }

      const finalSize = 400;
      const canvas = document.createElement('canvas');
      canvas.width = finalSize;
      canvas.height = finalSize;
      const ctx = canvas.getContext('2d');

      const scaleX = originalImage.width / displaySize.width;
      const scaleY = originalImage.height / displaySize.height;

      const cropLeft = cropPosition.x;
      const cropTop = cropPosition.y;
      const cropCenterX = cropLeft + cropSize / 2;
      const cropCenterY = cropTop + cropSize / 2;

      const imageLeft = (CONTAINER_SIZE - displaySize.width) / 2;
      const imageTop = (CONTAINER_SIZE - displaySize.height) / 2;

      const pointInUnzoomedX = cropCenterX - imageLeft;
      const pointInUnzoomedY = cropCenterY - imageTop;

      let sourceCenterX = pointInUnzoomedX * scaleX;
      let sourceCenterY = pointInUnzoomedY * scaleY;

      const cropSizeInOriginal = cropSize * scaleX;
      const halfCrop = cropSizeInOriginal / 2;

      sourceCenterX = Math.max(
        halfCrop,
        Math.min(sourceCenterX, originalImage.width - halfCrop)
      );
      sourceCenterY = Math.max(
        halfCrop,
        Math.min(sourceCenterY, originalImage.height - halfCrop)
      );

      const sourceX = sourceCenterX - halfCrop;
      const sourceY = sourceCenterY - halfCrop;

      ctx.drawImage(
        originalImage,
        sourceX,
        sourceY,
        cropSizeInOriginal,
        cropSizeInOriginal,
        0,
        0,
        finalSize,
        finalSize
      );

      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const handleSave = async () => {
    if (!originalImage) {
      setError('No hay imagen para guardar');
      return;
    }

    // Validar que tengamos los datos necesarios
    if (!tipoEmpleado) {
      setError('No se ha especificado el tipo de empleado');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const croppedBlob = await getCroppedImageBlob();

      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');

      if (credencialId) {
        formData.append('credencial_id', credencialId);
      }

      // Enviar los datos del empleado para generar el nombre correcto
      if (nombres) formData.append('nombres', nombres);
      if (apellidoPaterno) formData.append('apellido_paterno', apellidoPaterno);
      if (apellidoMaterno) formData.append('apellido_materno', apellidoMaterno);
      if (tipoEmpleado) formData.append('tipo_empleado', tipoEmpleado);

      const response = await api.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percent);
          }
        },
      });

      if (response.data && response.data.success && response.data.url) {
        if (onImageSaved) {
          // Devuelve la URL con timestamp para forzar recarga
          const urlWithTimestamp = `${response.data.url}?t=${new Date().getTime()}`;
          onImageSaved(urlWithTimestamp);
        }
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      setError(
        err.response?.data?.detail || err.message || 'Error al subir la imagen'
      );
    } finally {
      setUploading(false);
    }
  };

  const getImageContainerStyle = () => ({
    width: `${CONTAINER_SIZE}px`,
    height: `${CONTAINER_SIZE}px`,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#1a1a1a',
    borderRadius: '0.75rem',
    cursor: isDragging ? 'grabbing' : 'default',
  });

  const getImageStyle = () => {
    if (displaySize.width === 0) return { display: 'none' };

    return {
      width: `${displaySize.width}px`,
      height: `${displaySize.height}px`,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginLeft: `-${displaySize.width / 2}px`,
      marginTop: `-${displaySize.height / 2}px`,
    };
  };

  const getCropAreaStyle = () => {
    return {
      left: `${cropPosition.x}px`,
      top: `${cropPosition.y}px`,
      width: `${cropSize}px`,
      height: `${cropSize}px`,
      position: 'absolute',
      cursor: isDragging ? 'grabbing' : 'grab',
    };
  };

  const getMaskStyle = () => {
    const cropLeft = cropPosition.x;
    const cropTop = cropPosition.y;

    return {
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      mask: `rect(0px, ${CONTAINER_SIZE}px, ${CONTAINER_SIZE}px, 0px) exclude rect(${cropTop}px, ${cropLeft + cropSize}px, ${cropTop + cropSize}px, ${cropLeft}px)`,
      WebkitMask: `rect(0px, ${CONTAINER_SIZE}px, ${CONTAINER_SIZE}px, 0px) exclude rect(${cropTop}px, ${cropLeft + cropSize}px, ${cropTop + cropSize}px, ${cropLeft}px)`,
      pointerEvents: 'none',
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky z-10 top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              Editar foto de perfil
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Tipo de empleado:{' '}
              <span className="font-semibold text-blue-600">
                {tipoEmpleado || 'No especificado'}
              </span>
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {!originalImage && (
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                error
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleChangeImage}
            >
              <div className="text-6xl mb-4">📸</div>
              <p className="text-gray-600 mb-2 font-medium">
                Arrastra una imagen aquí o haz clic para seleccionar
              </p>
              <p className="text-xs text-gray-400">
                Formatos: JPG, PNG, WEBP • Máx. 5MB • Se recomienda una foto
                clara
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {originalImage && !uploading && (
            <div className="space-y-6">
              <div className="flex flex-col items-center">
                <div className="text-sm font-medium text-gray-700 mb-2 text-center">
                  Vista previa (resultado final)
                </div>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-75"></div>
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border-4 border-white shadow-xl bg-gray-100">
                    {finalPreview ? (
                      <img
                        src={finalPreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <svg
                          className="w-8 h-8 text-gray-400 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Así se verá la foto cuadrada en la credencial
                </p>
              </div>

              <canvas ref={previewCanvasRef} style={{ display: 'none' }} />

              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex gap-3">
                  <button
                    onClick={handleChangeImage}
                    className="flex-1 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition font-medium"
                  >
                    Cambiar imagen
                  </button>
                  <button
                    onClick={resetCrop}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Restablecer
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  💡 Arrastra el cuadro para moverlo. Arrastra las esquinas para
                  cambiar su tamaño (manteniendo la forma cuadrada).
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 text-center">
                  Arrastra el cuadro blanco o sus esquinas para seleccionar el
                  área
                </p>
                <div className="flex justify-center">
                  <div
                    ref={imageContainerRef}
                    style={getImageContainerStyle()}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <div style={getImageStyle()}>
                      <img
                        src={originalImageUrl}
                        alt="Editar"
                        className="select-none pointer-events-none"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                        draggable={false}
                      />
                    </div>

                    <div style={getMaskStyle()} />

                    <div
                      ref={cropAreaRef}
                      className="border-2 border-white shadow-lg"
                      style={getCropAreaStyle()}
                      onMouseDown={handleMouseDown}
                    >
                      <div
                        className="absolute -top-1 -left-1 w-4 h-4 cursor-nw-resize bg-white rounded-sm"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
                      ></div>
                      <div
                        className="absolute -top-1 -right-1 w-4 h-4 cursor-ne-resize bg-white rounded-sm"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
                      ></div>
                      <div
                        className="absolute -bottom-1 -left-1 w-4 h-4 cursor-sw-resize bg-white rounded-sm"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
                      ></div>
                      <div
                        className="absolute -bottom-1 -right-1 w-4 h-4 cursor-se-resize bg-white rounded-sm"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                      ></div>

                      <div className="absolute top-1/3 left-0 right-0 h-px bg-white/50 pointer-events-none"></div>
                      <div className="absolute bottom-1/3 left-0 right-0 h-px bg-white/50 pointer-events-none"></div>
                      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/50 pointer-events-none"></div>
                      <div className="absolute right-1/3 top-0 bottom-0 w-px bg-white/50 pointer-events-none"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {uploading && (
            <div className="text-center py-8">
              <div className="inline-block">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mx-auto mb-2">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-gray-600">
                  Guardando foto... {uploadProgress}%
                </p>
              </div>
            </div>
          )}

          {originalImage && !uploading && (
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={onCancel}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-lg"
              >
                Guardar foto
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
